// src/pages/Minpaku.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * 民泊一覧ページ
 * - API: GET /api/public/minpaku?page=<n>&limit=<m>
 *   { data: [...], total: number }
 * - スマホ/PCで表示件数切替
 * - 導入文 + まとめ文あり
 */
const Minpaku = () => {
  const mq = useMemo(() => window.matchMedia("(max-width: 767px)"), []);
  const initialPerPage = mq.matches ? 5 : 10;

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialPerPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const onChange = (e) => setItemsPerPage(e.matches ? 5 : 10);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mq]);

  const abortRef = useRef(null);

  useEffect(() => {
    async function fetchList() {
      setLoading(true);
      setError("");

      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const res = await fetch(
          `/api/public/minpaku?page=${page}&limit=${itemsPerPage}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setList(Array.isArray(json.data) ? json.data : []);
        setTotal(typeof json.total === "number" ? json.total : 0);
      } catch (err) {
        if (err.name !== "AbortError") {
          setList([]);
          setTotal(0);
          setError("民泊一覧の取得に失敗しました。時間をおいて再度お試しください。");
        }
      } finally {
        setLoading(false);
      }
    }
    fetchList();
  }, [page, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <h1 className="text-2xl font-bold mb-6 text-center">民泊一覧</h1>

      {/* 導入文 */}
      <section className="mb-10 text-sm leading-7 text-gray-700">
        <p className="mb-2">
          当社は、<strong>河口湖</strong>・<strong>富士吉田市</strong>を中心に、
          富士山周辺で快適にご滞在いただける民泊施設を展開しております。
        </p>
        <p>
          本ページでは、各施設の写真や特徴を一覧からご確認いただけます。
          観光・レジャー・ビジネスなど、さまざまな目的に合わせた
          滞在先選びにご活用ください。
        </p>
      </section>

      {/* 読み込み・エラー・データなし */}
      {loading && <p className="text-center text-gray-600 mb-6">読み込み中・・・</p>}
      {!!error && <p className="text-center text-red-600 mb-6">{error}</p>}
      {!loading && !error && list.length === 0 && (
        <p className="text-center text-gray-600 mb-6">
          現在、公開中の民泊情報はありません。/ 読み込み中・・・
        </p>
      )}

      {/* 一覧 */}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {list.map((item) => {
          const photo = item?.photos?.[0] || "";
          const name = item?.name || "施設名未設定";
          const dateText = item?.createdAt
            ? new Date(item.createdAt).toLocaleDateString("ja-JP", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "";

          return (
            <li key={item.id} className="border rounded overflow-hidden hover:shadow-md transition">
              <Link
                to={`/businessContent/minpaku/${item.id}`}
                className="group block"
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={`${name}の外観写真`}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-400">
                    画像準備中
                  </div>
                )}
                <div className="p-4">
                  <h2 className="text-xl font-semibold">{name}</h2>
                  {dateText && (
                    <p className="text-sm text-gray-600 mb-2">{dateText}</p>
                  )}
                  <span className="inline-block text-blue-600 group-hover:underline">
                    詳細を見る →
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      {/* ページャー */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center items-center gap-4">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1 || loading}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            ‹ 前へ
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page === totalPages || loading}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            次へ ›
          </button>
        </div>
      )}

      {/* まとめ文 */}
      <section className="mt-12 text-sm leading-7 text-gray-700">
        <p className="mb-2">
          掲載している施設はすべて河口湖・富士吉田エリアを中心に運営しております。
        </p>
        <p className="mb-2">
          富士山観光の拠点や、ご家族・ご友人との旅行、
          ビジネスでのご利用にも最適です。
        </p>
        <p>
          一覧から詳細をご確認いただき、
          ご希望に合った宿泊先をぜひお選びください。
        </p>
      </section>
    </div>
  );
};

export default Minpaku;
